import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import data from "../../data/index.json";
import images from "../../constants/images";
import { Section, SectionHeader, reveal } from "../../components";

/**
 * A two-person leadership team is better served by generous portraits than by
 * a grid of small avatars — so each member gets a full editorial row.
 */
const Team = () => {
  const { team } = data;
  const { t } = useTranslation();

  return (
    <Section id="team" tone="warm">
      <SectionHeader
        index="07"
        label={t("The People")}
        title={
          <>
            {t("Executive")} <em>{t("leadership")}</em>
          </>
        }
        lede={t("Driven by expertise. Guided by vision. Committed to excellence.")}
      />

      {team && team.length > 0 ? (
        <div className="mt-14 md:mt-20">
          {team.map((member, index) => (
            <motion.article
              key={member.name}
              {...reveal(index)}
              className="group grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 items-start py-12 md:py-16 border-t border-[color:var(--rule)]"
            >
              <div className="md:col-span-4 lg:col-span-3">
                <div className="overflow-hidden bg-paper-tint aspect-[3/4] max-w-[280px]">
                  <img
                    src={member.img}
                    alt={`${member.name}, ${t(member.position)}`}
                    loading="lazy"
                    className="img-editorial"
                    style={{ objectPosition: "center top" }}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = images.fallbackImage;
                    }}
                  />
                </div>
              </div>

              <div className="md:col-span-8 lg:col-span-9">
                <span className="label tabular text-gold-ink">
                  {String(index + 1).padStart(2, "0")}
                </span>

                <h3 className="display mt-3">{t(member.name)}</h3>

                <p className="label mt-3">{t(member.position)}</p>

                <p className="prose-body mt-6">{t(member.details)}</p>

                <p className="mt-6 pt-4 border-t border-[color:var(--rule)] text-xs text-ink-faint max-w-measure">
                  {t("Focus")} — {t(member.expertise)}
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      ) : (
        <p className="mt-16 py-16 text-center text-ink-faint border-t border-[color:var(--rule)]">
          {t("No team members found.")}
        </p>
      )}
    </Section>
  );
};

export default Team;
